import { useEffect, useRef } from "react";
import { useLogin, useTranslate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import {
  getOAuthSession,
  getOAuthSessionAccessToken,
  getOAuthErrorType,
  OAUTH_ERROR_SESSION_KEY,
} from "services/oauth";
import {
  clearPendingAuthMethod,
  getPendingAuthMethod,
} from "services/pendingAuthMethod";
import { LoadingSpinner } from "components/LoadingSpinner";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import type { AuthMethod } from "types/analytics";

const getProviderFromPath = () => {
  const segment = globalThis.location?.pathname.split("/auth/callback/")[1];
  if (!segment) return "";
  return segment.charAt(0).toUpperCase() + segment.slice(1);
};

export const OAuthCallbackPage = () => {
  const { trackEvent } = useAnalytics();
  // Shared across handleCallback → onSuccess / onError since login() is async
  const pendingProvider = useRef<AuthMethod | null>(null);
  const { mutate: login } = useLogin({
    mutationOptions: {
      onError: () => {
        trackEvent(AnalyticsEvents.LoginFailed, {
          login_method: pendingProvider.current ?? undefined,
          reason: "oauth_error",
        });
      },
    },
  });
  const navigate = useNavigate();
  const t = useTranslate();
  const provider = getProviderFromPath();

  const handleOAuthCallback = async () => {
    try {
      const session = await getOAuthSession();
      pendingProvider.current = getPendingAuthMethod();
      clearPendingAuthMethod();
      const access_token = getOAuthSessionAccessToken(session);
      // Hub listener won't fire here (OAuth redirect succeeded),
      // and login() hasn't been called yet, so this is the only tracking point.
      if (!access_token) {
        trackEvent(AnalyticsEvents.LoginFailed, {
          login_method: pendingProvider.current,
          reason: "oauth_error",
          error_type: "no_session_token",
        });
        throw new Error(t("pages.auth.errors.oAuthNoSessionToken"));
      }
      return { access_token, token_type: "bearer" as const };
    } catch (error) {
      console.error(t("pages.auth.errors.oauthError"), error);
      throw error;
    }
  };

  /**
   * Flow:
    1. Browser redirects to /auth/callback/google?code=xxx&state=yyy
    2. initOAuth() already ran (index.tsx line 22)
    3. Amplify intercepts, exchanges code for tokens, clears URL
    4. URL is now /auth/callback/google (no query params)
    5. React renders OAuthCallbackPage
    6. useEffect runs handleCallback()
    7. location.search is "" → code is null
   */
  const handleCallback = async () => {
    try {
      const token = await handleOAuthCallback();
      login({ provider: provider.toLowerCase(), token, redirect: true });
    } catch (error) {
      const oauthError = sessionStorage.getItem(OAUTH_ERROR_SESSION_KEY);
      sessionStorage.removeItem(OAUTH_ERROR_SESSION_KEY);

      const errorType = getOAuthErrorType(oauthError);

      if (errorType === "email_account_exists") {
        navigate("/login", {
          state: { oauth_error: "email_account_exists", provider },
        });
        return;
      }

      if (errorType === "account_locked") {
        navigate("/login", {
          state: { oauth_error: "account_locked" },
        });
        return;
      }

      message.error((error as Error).message ?? t("pages.auth.errors.generic"));
      navigate("/");
    }
  };

  useEffect(() => {
    void handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <LoadingSpinner />;
};
